import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import {
  ActionButton,
  Button,
  CustomDialog,
  DialogContainer,
} from '@react-spectrum/s2'
import { AlertCircle, ArrowDown, Menu, MessageSquare, X } from 'lucide-react'
import { z } from 'zod'
import {
  CONVERSATION_ACCESS_DENIED_ERROR,
  deleteConversation,
  getConversations,
  getDiagramRequests,
  getActiveChatGeneration,
  getMessages,
  getTableRequests,
  resolveSourceReferenceRequest,
} from '@rpc/chat.rpc'
import { checkAdminAccess } from '@rpc/admin-check.rpc'
import { getUserUsageStatus } from '@rpc/user.rpc'
import { getAuthSession } from '@rpc/auth-session.rpc'
import { getPersonaProfile } from '@rpc/persona.rpc'
import {
  getAnalyticsRequestContext,
  trackAnalyticsEvent,
} from '@client/analytics'
import {
  CHAT_QUICK_PROMPTS,
  pickRandomQuickPrompts,
} from '@shared/chat-quick-prompts'
import { consumeHomeChatHandoff } from '@client/chat-handoff'
import type {
  GroundedCitation,
  GroundingSupport,
} from '@shared/grounded-chat-types'
import type { HistoryMessage, MessageFeedbackSummary } from '@shared/chat-types'
import { ChatMessage } from '@/components/ChatMessage'
import { ChatInput } from '@/components/ChatInput'
import { CitationPanel } from '@/components/CitationPanel'
import { ChatSidebar } from '@/components/ChatSidebar'
import Header from '@/components/Header'
import { MobileChatHistory } from '@/components/MobileChatHistory'
import { QuickPromptCards } from '@/components/QuickPromptCards'
import { PersonaPromptCard } from '@/components/PersonaPromptCard'
import {
  SourcesCoachmark,
  type SourcesCoachmarkPlacement,
  type SourcesCoachmarkRect,
} from '@/components/SourcesCoachmark'
import { SourcesToggle } from '@/components/SourcesToggle'
import BetterAuthHeader from '@/integrations/better-auth/header-user.tsx'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/use-media-query'
import { useKeyboardViewportInset } from '@/hooks/use-keyboard-viewport-inset'
import { formatStatusText } from '@/lib/text-formatting'
import { artifactPreviewDialogStyles } from '@/styles/s2-macros'

const chatSearchSchema = z.object({
  conversationId: z.string().optional(),
  source: z.enum(['home']).optional(),
  planCheckRunId: z.string().optional(),
  planCheckTitle: z.string().optional(),
})

const STREAM_SLOW_WARNING_MS = 30_000
const SOURCES_VISIBLE_STORAGE_KEY = 'buildingregs:sources-visible'
const CHAT_COMPOSER_FALLBACK_HEIGHT = 152
const CHAT_MESSAGE_BOTTOM_BUFFER_PX = 16
const CHAT_MOBILE_MESSAGE_BOTTOM_BUFFER_PX = 24
const CHAT_NEAR_BOTTOM_THRESHOLD_PX = 160
const SOURCES_COACHMARK_DISMISS_MS = 10_000
const SOURCES_COACHMARK_SPOTLIGHT_MS = 1_200

function getUsageAnalyticsProperties(
  usage: { count: number; limit: number; remaining: number } | undefined,
) {
  return {
    usage_count: usage?.count ?? null,
    usage_limit: usage?.limit ?? null,
    usage_remaining: usage?.remaining ?? null,
  }
}
const SOURCE_COACH_TARGET_SELECTOR = '[data-source-coach-target="true"]'

type ChatHttpError = Error & {
  code: string
  stage: string
}

async function createChatHttpError(response: Response): Promise<ChatHttpError> {
  let responseText = ''
  try {
    responseText = await response.text()
  } catch {
    // The status is still enough to provide a useful, non-silent failure.
  }

  let responseMessage: string | null = null
  try {
    const body = JSON.parse(responseText) as {
      error?: unknown
      message?: unknown
    }
    const candidate = body.error ?? body.message
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      responseMessage = candidate.trim()
    }
  } catch {
    // Non-JSON proxy and middleware responses use the status-specific message below.
  }

  const isGenericServerMessage =
    responseMessage === 'Internal Server Error' ||
    responseMessage === 'Forbidden'
  const message =
    response.status === 403
      ? 'Your chat request was blocked. Refresh the page and try again.'
      : response.status >= 500
        ? 'The chat service could not start a response. Please try again later.'
        : !isGenericServerMessage && responseMessage
          ? responseMessage
          : `The chat request failed (HTTP ${response.status}). Please try again.`

  const error = new Error(message) as ChatHttpError
  error.code = `http_${response.status}`
  error.stage = 'starting_stream'
  return error
}

type SourcesCoachmarkState = {
  activationId: number
  placement: SourcesCoachmarkPlacement
  targetIndex: number
  targetRect: SourcesCoachmarkRect
}

function buildChatConversationReturnTo(conversationId: string) {
  return `/chat?conversationId=${encodeURIComponent(conversationId)}`
}

function toCoachmarkRect(rect: DOMRect): SourcesCoachmarkRect {
  return {
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height,
  }
}

function isElementVisibleInContainer(
  element: HTMLElement,
  container: HTMLElement,
) {
  const rect = element.getBoundingClientRect()
  if (rect.width === 0 && rect.height === 0) return true

  const containerRect = container.getBoundingClientRect()
  const viewportHeight =
    typeof window === 'undefined' ? 768 : window.innerHeight || 768
  const viewportWidth =
    typeof window === 'undefined' ? 1024 : window.innerWidth || 1024
  const visibleTop = Math.max(containerRect.top, 0)
  const visibleBottom = Math.min(containerRect.bottom, viewportHeight)
  const visibleLeft = Math.max(containerRect.left, 0)
  const visibleRight = Math.min(containerRect.right, viewportWidth)

  return (
    rect.bottom > visibleTop &&
    rect.top < visibleBottom &&
    rect.right > visibleLeft &&
    rect.left < visibleRight
  )
}

function getStoredSourcesVisible() {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(SOURCES_VISIBLE_STORAGE_KEY) === 'true'
}

function getCurrentConversationUrl() {
  return typeof window === 'undefined' ? null : window.location.href
}

function applyCitationSelection(
  messageCitations: Array<GroundedCitation>,
  chunkIndices: Array<number>,
  visualEvidence: Array<any>,
  onSelect: (selection: {
    citations: Array<GroundedCitation>
    chunkIndices: Array<number>
    visualEvidence: Array<any>
  }) => void,
) {
  const availableChunkIndices = chunkIndices.filter(
    (chunkIndex) =>
      Number.isInteger(chunkIndex) &&
      chunkIndex >= 0 &&
      chunkIndex < messageCitations.length,
  )
  if (availableChunkIndices.length === 0) return

  onSelect({
    citations: messageCitations,
    chunkIndices: availableChunkIndices,
    visualEvidence,
  })
}

function replaceResolvedSourceReference(
  message: HydratedMessage,
  resolved: any,
): HydratedMessage {
  if (!message.id || !Array.isArray(message.sourceReferenceRequests)) {
    return message
  }

  const requestIndex = message.sourceReferenceRequests.findIndex(
    (item: any) => item.id === resolved.id,
  )
  if (requestIndex < 0) return message

  const nextRequests = [...message.sourceReferenceRequests]
  nextRequests[requestIndex] = resolved
  return { ...message, sourceReferenceRequests: nextRequests }
}

function updateConnectedCoachmarkTarget(
  cachedTarget: HTMLElement | null,
  getFallbackTarget: () => HTMLElement | null,
  onRemoved: (reason: 'target_removed') => void,
  onConnected: (target: HTMLElement) => void,
) {
  const target =
    cachedTarget?.isConnected === true ? cachedTarget : getFallbackTarget()
  if (!target || !target.isConnected) {
    onRemoved('target_removed')
    return
  }
  onConnected(target)
}

function getCoachmarkTarget(container: HTMLElement, targetIndex: number) {
  return (
    Array.from(
      container.querySelectorAll<HTMLElement>(SOURCE_COACH_TARGET_SELECTOR),
    )[targetIndex] ?? null
  )
}

function activateCoachmarkPulse(
  target: HTMLElement | null,
  onTarget: (target: HTMLElement) => void,
) {
  if (!target) return
  onTarget(target)
}

type ConversationSummary = {
  id: string
  title?: string | null
}

type LoaderMessage = {
  id: string
  role: string
  content: string
  createdAt?: string | Date | null
  citations: string | null
  groundingSupports: string | null
  visualEvidence: string | null
  verificationStatus: string | null
  errorCode: string | null
  errorStage: string | null
  rawLLMResponse: string | null
  diagramRequests?: Array<any>
  tableRequests?: Array<any>
  sourceReferenceRequests?: Array<any>
  feedback?: MessageFeedbackSummary | null
}

type HydratedMessage = HistoryMessage & {
  id?: string
  createdAt?: string | Date | null
  rawContent?: string | null
  groundingSupports?: Array<any>
  visualEvidence?: Array<any>
  diagramRequests?: Array<any>
  tableRequests?: Array<any>
  sourceReferenceRequests?: Array<any>
  verificationStatus?: 'verifying' | 'verified' | 'unverified'
  errorCode?: string | null
  errorStage?: string | null
  feedback?: MessageFeedbackSummary | null
}

type CitationArtifact = {
  title: string
  pageStart?: number
  pageEnd?: number
  pageImageUrl?: string | null
  sourcePdfUrl?: string | null
}

type ArtifactPreviewState =
  | {
      kind: 'pdf'
      title: string
      url: string
      sourceUrl: string
      pageNumber?: number
    }
  | {
      kind: 'page-image'
      title: string
      url: string
      pageNumber?: number
    }

type SelectedSourceReferenceState =
  | {
      status: 'loading'
      referenceText: string
    }
  | ({
      status: 'ready'
    } & Record<string, any>)
  | ({
      status: 'failed'
    } & Record<string, any>)

function dedupeConversations<T extends ConversationSummary>(items: Array<T>) {
  const seen = new Set<string>()
  return items.filter((item) => {
    if (!item.id || seen.has(item.id)) return false
    seen.add(item.id)
    return true
  })
}

function hydrateMessage(message: LoaderMessage): HydratedMessage {
  const role: HistoryMessage['role'] =
    message.role === 'assistant' ? 'assistant' : 'user'
  let rawContent: string | null = null
  if (message.rawLLMResponse) {
    try {
      const parsed = JSON.parse(message.rawLLMResponse) as Record<
        string,
        unknown
      > | null
      if (parsed && typeof parsed.raw_text === 'string') {
        rawContent = parsed.raw_text
      }
    } catch {
      rawContent = null
    }
  }

  return {
    id: (message as { id?: string }).id,
    createdAt: message.createdAt ?? null,
    role,
    content: message.content,
    rawContent,
    citations: message.citations ? JSON.parse(message.citations) : [],
    groundingSupports: message.groundingSupports
      ? JSON.parse(message.groundingSupports)
      : undefined,
    visualEvidence: message.visualEvidence
      ? JSON.parse(message.visualEvidence)
      : undefined,
    diagramRequests: message.diagramRequests ?? [],
    tableRequests: message.tableRequests ?? [],
    sourceReferenceRequests: message.sourceReferenceRequests ?? [],
    feedback: message.feedback ?? null,
    verificationStatus:
      message.role === 'assistant'
        ? ((message.verificationStatus as
            | 'verifying'
            | 'verified'
            | 'unverified'
            | null) ?? 'unverified')
        : undefined,
    errorCode: message.errorCode,
    errorStage: message.errorStage,
  }
}

function withPdfPageFragment(url: string, pageNumber?: number) {
  if (!pageNumber || pageNumber < 1) return url
  const fragment = `page=${pageNumber}`
  return url.includes('#') ? `${url}&${fragment}` : `${url}#${fragment}`
}

function buildSinglePageDownloadUrl(url: string, pageNumber?: number) {
  if (!pageNumber || pageNumber < 1) return null
  const [baseUrl] = url.split('#')
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}downloadPage=${pageNumber}`
}

function parseSseBlock(block: string) {
  const lines = block
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter(Boolean)

  if (lines.length === 0) return null

  const hasSseMarkers = lines.some(
    (line) =>
      line.startsWith('event:') ||
      line.startsWith('data:') ||
      line.startsWith(':'),
  )
  if (!hasSseMarkers) return null

  const dataLines = lines
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())

  if (dataLines.length === 0) return null

  try {
    return JSON.parse(dataLines.join('\n'))
  } catch {
    return null
  }
}

function parseLegacyJsonLine(line: string) {
  const trimmed = line.trim()
  if (!trimmed.startsWith('{')) return null

  try {
    return JSON.parse(trimmed)
  } catch {
    return null
  }
}

function consumeStructuredStreamPayloads(input: string, flush = false) {
  const payloads: Array<any> = []
  let buffer = input

  for (;;) {
    const match = buffer.match(/\r?\n\r?\n/)
    if (!match || match.index === undefined) break

    const block = buffer.slice(0, match.index)
    buffer = buffer.slice(match.index + match[0].length)

    const ssePayload = parseSseBlock(block)
    if (ssePayload !== null) {
      payloads.push(ssePayload)
      continue
    }

    for (const line of block.split(/\r?\n/)) {
      const payload = parseLegacyJsonLine(line)
      if (payload !== null) {
        payloads.push(payload)
      }
    }
  }

  const lines = buffer.split(/\r?\n/)
  const trailingLine = lines.pop()!
  buffer = flush ? '' : trailingLine
  const completeLines = flush
    ? lines.concat(trailingLine ? [trailingLine] : [])
    : lines

  for (const line of completeLines) {
    const payload = parseLegacyJsonLine(line)
    if (payload !== null) {
      payloads.push(payload)
    }
  }

  return { payloads, remainingBuffer: flush ? '' : buffer }
}

function readErrorString(error: unknown, key: string) {
  if (typeof error !== 'object' || error === null || !(key in error)) {
    return undefined
  }
  const value = (error as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : undefined
}

function isMarkedStreamError(error: unknown) {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('isStreamError' in error)
  ) {
    return false
  }
  return Boolean((error as { isStreamError?: unknown }).isStreamError)
}

function MobileSheet(props: {
  title?: string
  onClose: () => void
  children: ReactNode
  headerContent?: ReactNode
}) {
  const { title, onClose, children, headerContent } = props

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  /* v8 ignore start -- chat page shell markup */
  return (
    <div
      className="fixed inset-0 z-50 flex h-[100svh] max-w-full flex-col overflow-hidden bg-background text-foreground lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={title ?? 'Mobile panel'}
      data-testid="mobile-sheet"
    >
      <div
        className="flex shrink-0 items-center justify-between gap-3 border-b border-border/80 bg-background px-4 pb-3 shadow-sm"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        {headerContent ? (
          <div className="min-w-0 flex-1">{headerContent}</div>
        ) : (
          <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">
            {title}
          </h2>
        )}
        <button
          type="button"
          aria-label={title ? `Close ${title}` : 'Close panel'}
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
          onClick={onClose}
        >
          <X className="h-4 w-4" aria-hidden />
          <span>Close</span>
        </button>
      </div>
      <div
        className="min-h-0 flex-1 overflow-hidden overscroll-contain bg-background"
        style={{
          paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
        }}
      >
        {children}
      </div>
    </div>
  )
  /* v8 ignore stop -- chat page shell markup */
}

function MobileHistoryDrawer(props: {
  title?: string
  onClose: () => void
  children: ReactNode
  headerContent?: ReactNode
}) {
  const { title = 'Navigation', onClose, children, headerContent } = props

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  /* v8 ignore start -- chat page shell markup */
  return (
    <div
      id="mobile-chat-history-drawer"
      className="fixed inset-0 z-50 lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="mobile-history-drawer"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-foreground/20"
        aria-label="Close chat history"
        onClick={onClose}
      />
      <div className="relative flex h-[100dvh] w-[min(20rem,calc(100vw-3rem))] min-w-0 flex-col overflow-hidden rounded-r-2xl border-r border-border/80 bg-background text-foreground shadow-[0_24px_64px_-32px_rgba(15,23,42,0.45)]">
        <div
          className="flex shrink-0 items-center justify-between border-b border-border/80 bg-background px-4 pb-3"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          {headerContent ? (
            <div className="min-w-0 flex-1">{headerContent}</div>
          ) : (
            <h2 className="truncate text-base font-semibold text-foreground">
              {title}
            </h2>
          )}
          <ActionButton aria-label={`Close ${title}`} isQuiet onPress={onClose}>
            <X className="h-5 w-5" />
          </ActionButton>
        </div>
        <div
          className="min-h-0 flex-1 overflow-hidden overscroll-contain bg-background"
          style={{
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
  /* v8 ignore stop -- chat page shell markup */
}

export const Route = createFileRoute('/chat')({
  validateSearch: (search) => chatSearchSchema.parse(search),
  loaderDeps: ({ search: { conversationId } }) => ({ conversationId }),
  loader: async ({ deps: { conversationId } }) => {
    let initialMessages: Array<LoaderMessage> = []
    let initialGeneration: any = null
    if (conversationId) {
      try {
        const [loadedMessages, loadedGeneration] = await Promise.all([
          getMessages({ data: { conversationId } }),
          getActiveChatGeneration({ data: { conversationId } }),
        ])
        initialMessages = loadedMessages
        initialGeneration = loadedGeneration
      } catch (error) {
        if (
          error instanceof Error &&
          error.message === CONVERSATION_ACCESS_DENIED_ERROR
        ) {
          const isAdmin = await checkAdminAccess().catch(() => false)
          if (isAdmin) {
            throw redirect({
              to: '/admin/impersonate-conversation',
              search: {
                conversationId,
                returnTo: buildChatConversationReturnTo(conversationId),
              },
              replace: true,
            })
          }

          throw redirect({
            to: '/chat',
            search: {},
            replace: true,
          })
        }
        throw error
      }
    }

    const [conversations, session, initialUsage, personaProfile] =
      await Promise.all([
        getConversations(),
        getAuthSession(),
        getUserUsageStatus(),
        getPersonaProfile(),
      ])

    return {
      initialConversations: dedupeConversations(
        conversations as Array<ConversationSummary>,
      ),
      initialQuickPrompts: pickRandomQuickPrompts(CHAT_QUICK_PROMPTS, 3),
      user: session?.user,
      initialMessages,
      initialGeneration,
      initialUsage,
      personaProfile,
    }
  },
  component: ChatPage,
  head: () => ({
    meta: [
      { title: 'Chat - Building regs assistant' },
      {
        name: 'description',
        content:
          'Ask BuildingRegs.app questions about UK building regulations and get cited answers from approved documents, standards, and guidance.',
      },
    ],
  }),
})

function ChatPage() {
  const {
    initialConversations,
    initialQuickPrompts,
    user,
    initialMessages,
    initialGeneration,
    initialUsage,
    personaProfile,
  } = Route.useLoaderData()
  const { conversationId, source, planCheckRunId, planCheckTitle } =
    Route.useSearch()
  const navigate = useNavigate()
  const hydratedConversationRef = useRef(conversationId)
  const autoSentFromHomeRef = useRef(false)

  const [messages, setMessages] = useState<Array<HydratedMessage>>(
    initialMessages.map(hydrateMessage),
  )
  const [conversations, setConversations] = useState(() =>
    dedupeConversations(initialConversations),
  )
  const [activeId, setActiveId] = useState(conversationId)
  const [emptyStateQuickPrompts] = useState(initialQuickPrompts)
  const [draftPrompt, setDraftPrompt] = useState<{
    key: number
    value: string
  } | null>(null)

  // State for loading and status
  const [isLoading, setIsLoading] = useState(Boolean(initialGeneration))
  const [statusText, setStatusText] = useState('Thinking...')
  const [statusReasoningSummary, setStatusReasoningSummary] = useState<
    string | null
  >(null)
  const [usage, setUsage] = useState<
    { count: number; limit: number; remaining: number } | undefined
  >(initialUsage)

  // Modals & Mobile Sheets
  const [showPaywall, setShowPaywall] = useState(false)
  const [isMobileHistoryOpen, setIsMobileHistoryOpen] = useState(false)
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const keyboardViewportInset = useKeyboardViewportInset()
  const [sourcesVisible, setSourcesVisible] = useState(getStoredSourcesVisible)
  const [showPersonaPrompt, setShowPersonaPrompt] = useState(
    Boolean(
      user &&
      personaProfile &&
      !personaProfile.persona &&
      !personaProfile.dismissedAt,
    ),
  )

  const [citations, setCitations] = useState<Array<GroundedCitation>>([])
  const [visualEvidence, setVisualEvidence] = useState<Array<any>>([])
  const [isCitationOpen, setIsCitationOpen] = useState(false)
  const [filteredChunkIndices, setFilteredChunkIndices] =
    useState<Array<number> | null>(null)
  const [artifactPreview, setArtifactPreview] =
    useState<ArtifactPreviewState | null>(null)
  const [selectedSourceReference, setSelectedSourceReference] =
    useState<SelectedSourceReferenceState | null>(null)
  const scopedPlanCheckSearch = planCheckRunId
    ? { planCheckRunId, planCheckTitle }
    : {}

  const scrollRef = useRef<HTMLDivElement>(null)
  const messageContentRef = useRef<HTMLDivElement>(null)
  const bottomComposerRef = useRef<HTMLDivElement>(null)
  // Track if paywall has been shown this session to prevent loop
  const hasShownPaywallRef = useRef(false)
  const diagramPollingRef = useRef<number | null>(null)
  const tablePollingRef = useRef<number | null>(null)
  const activeStreamAbortControllerRef = useRef<AbortController | null>(null)
  const activeGenerationIdRef = useRef<string | null>(
    initialGeneration?.generation.id ?? null,
  )
  const activeGenerationEventIdRef = useRef<number>(
    initialGeneration?.lastEventId ?? 0,
  )
  const [composerHeight, setComposerHeight] = useState(
    CHAT_COMPOSER_FALLBACK_HEIGHT,
  )
  const [showJumpToLatest, setShowJumpToLatest] = useState(false)
  const [sourcesCoachmark, setSourcesCoachmark] =
    useState<SourcesCoachmarkState | null>(null)
  const [sourcesCoachmarkActivationId, setSourcesCoachmarkActivationId] =
    useState(0)
  const sourcesCoachmarkTargetRef = useRef<HTMLElement | null>(null)

  const isLimitReached = usage
    ? usage.limit > 0 && usage.count >= usage.limit
    : false
  const hasAssistantResponse = messages.some(
    (message) => message.role === 'assistant',
  )
  const personaPromptAfterIndex = showPersonaPrompt
    ? messages.findIndex(
        (message) =>
          message.role === 'assistant' &&
          message.verificationStatus === 'verified',
      )
    : -1

  const updateLatestButtonVisibility = useCallback(() => {
    const container = scrollRef.current
    if (!container) return

    const distanceFromBottom = Math.max(
      0,
      container.scrollHeight - container.scrollTop - container.clientHeight,
    )
    const isNearBottom = distanceFromBottom <= CHAT_NEAR_BOTTOM_THRESHOLD_PX
    setShowJumpToLatest(messages.length > 0 && !isNearBottom)
  }, [messages.length])

  const dismissSourcesCoachmark = useCallback((reason: string) => {
    setSourcesCoachmark((current) => {
      if (!current) return null
      trackAnalyticsEvent('sources_coachmark_dismissed', {
        placement: current.placement,
        reason,
      })
      sourcesCoachmarkTargetRef.current?.classList.remove(
        'source-coachmark-pulse',
      )
      sourcesCoachmarkTargetRef.current = null
      return null
    })
  }, [])

  const openSourcesCoachmarkForActivation = useCallback(
    (activationId: number) => {
      const container = scrollRef.current!

      const targets = Array.from(
        container.querySelectorAll<HTMLElement>(SOURCE_COACH_TARGET_SELECTOR),
      )
      if (targets.length === 0) return

      const lastTarget = targets.at(-1)
      let target =
        [...targets]
          .reverse()
          .find((element) => isElementVisibleInContainer(element, container)) ??
        null

      if (!target && !isDesktop && lastTarget) {
        target = lastTarget
        target.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }

      if (!target) return
      const selectedTarget = target
      const selectedTargetIndex = targets.indexOf(selectedTarget)

      const showCoachmark = () => {
        const targetRect = toCoachmarkRect(
          selectedTarget.getBoundingClientRect(),
        )
        const placement: SourcesCoachmarkPlacement = isDesktop
          ? 'desktop_inline'
          : 'mobile_bottom'

        sourcesCoachmarkTargetRef.current = selectedTarget
        setSourcesCoachmark({
          activationId,
          placement,
          targetIndex: selectedTargetIndex,
          targetRect,
        })
        trackAnalyticsEvent('sources_coachmark_shown', { placement })
      }

      if (!isDesktop && selectedTarget === lastTarget) {
        window.setTimeout(showCoachmark, 180)
        return
      }

      showCoachmark()
    },
    [isDesktop],
  )

  const getCurrentSourcesCoachmarkTarget = useCallback(
    (targetIndex: number) => {
      return getCoachmarkTarget(scrollRef.current!, targetIndex)
    },
    [],
  )

  const changeSourcesVisible = (next: boolean) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SOURCES_VISIBLE_STORAGE_KEY, String(next))
    }
    trackAnalyticsEvent(
      next ? 'sources_mode_enabled' : 'sources_mode_disabled',
      {
        location: isDesktop ? 'desktop_header' : 'mobile_header',
      },
    )
    setSourcesVisible(next)
    if (next) {
      setSourcesCoachmarkActivationId((currentId) => currentId + 1)
    } else {
      dismissSourcesCoachmark('sources_disabled')
    }
  }

  useEffect(() => {
    if (!sourcesVisible || sourcesCoachmarkActivationId === 0) return

    const frame = window.requestAnimationFrame(() => {
      openSourcesCoachmarkForActivation(sourcesCoachmarkActivationId)
    })

    return () => window.cancelAnimationFrame(frame)
  }, [
    openSourcesCoachmarkForActivation,
    sourcesCoachmarkActivationId,
    sourcesVisible,
  ])

  useEffect(() => {
    if (!sourcesCoachmark) return

    const timeout = window.setTimeout(() => {
      dismissSourcesCoachmark('timeout')
    }, SOURCES_COACHMARK_DISMISS_MS)

    return () => window.clearTimeout(timeout)
  }, [dismissSourcesCoachmark, sourcesCoachmark?.activationId])

  useEffect(() => {
    if (!sourcesCoachmark) return

    let pulseTimeout: number | null = null
    let pulsedTarget: HTMLElement | null = null
    const frame = window.requestAnimationFrame(() => {
      const target = getCurrentSourcesCoachmarkTarget(
        sourcesCoachmark.targetIndex,
      )
      activateCoachmarkPulse(target, (connectedTarget) => {
        sourcesCoachmarkTargetRef.current?.classList.remove(
          'source-coachmark-pulse',
        )
        sourcesCoachmarkTargetRef.current = connectedTarget
        pulsedTarget = connectedTarget
        connectedTarget.classList.add('source-coachmark-pulse')
        pulseTimeout = window.setTimeout(() => {
          connectedTarget.classList.remove('source-coachmark-pulse')
        }, SOURCES_COACHMARK_SPOTLIGHT_MS)
      })
    })

    return () => {
      window.cancelAnimationFrame(frame)
      if (pulseTimeout !== null) {
        window.clearTimeout(pulseTimeout)
      }
      pulsedTarget?.classList.remove('source-coachmark-pulse')
    }
  }, [
    getCurrentSourcesCoachmarkTarget,
    sourcesCoachmark?.activationId,
    sourcesCoachmark?.targetIndex,
  ])

  useEffect(() => {
    if (!sourcesCoachmark) return

    const updatePosition = () => {
      updateConnectedCoachmarkTarget(
        sourcesCoachmarkTargetRef.current,
        () => getCurrentSourcesCoachmarkTarget(sourcesCoachmark.targetIndex),
        dismissSourcesCoachmark,
        (target) => {
          sourcesCoachmarkTargetRef.current = target
          setSourcesCoachmark({
            ...sourcesCoachmark,
            targetRect: toCoachmarkRect(target.getBoundingClientRect()),
          })
        },
      )
    }

    const container = scrollRef.current
    container?.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)
    window.visualViewport?.addEventListener('resize', updatePosition)
    window.visualViewport?.addEventListener('scroll', updatePosition)

    return () => {
      container?.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
      window.visualViewport?.removeEventListener('resize', updatePosition)
      window.visualViewport?.removeEventListener('scroll', updatePosition)
    }
  }, [
    dismissSourcesCoachmark,
    getCurrentSourcesCoachmarkTarget,
    sourcesCoachmark?.activationId,
    sourcesCoachmark?.targetIndex,
  ])

  const refreshConversationFromServer = useCallback(
    async (conversationIdToRefresh: string) => {
      try {
        const persistedMessages = (await getMessages({
          data: { conversationId: conversationIdToRefresh },
        })) as Array<LoaderMessage>
        const hydratedMessages = persistedMessages.map(hydrateMessage)
        const latestPersistedMessage = hydratedMessages.at(-1)
        if (
          !latestPersistedMessage ||
          latestPersistedMessage.role !== 'assistant'
        ) {
          return false
        }

        const latestAssistantMessage = [...hydratedMessages]
          .reverse()
          .find((message) => message.role === 'assistant')

        setMessages(hydratedMessages)
        setActiveId(conversationIdToRefresh)
        hydratedConversationRef.current = conversationIdToRefresh
        setCitations(latestPersistedMessage.citations)
        setVisualEvidence(latestPersistedMessage.visualEvidence)
        return true
      } catch {
        return false
      }
    },
    [],
  )

  const resumeGenerationFromServer = useCallback(
    async (generationId: string, conversationIdToRefresh: string) => {
      activeGenerationIdRef.current = generationId
      setIsLoading(true)

      while (activeGenerationIdRef.current === generationId) {
        try {
          const response = await fetch(
            `/api/chat-generations/${encodeURIComponent(generationId)}/events?after=${activeGenerationEventIdRef.current}`,
            { headers: { Accept: 'text/event-stream' } },
          )
          if (!response.ok || !response.body) {
            throw new Error(
              `Unable to reconnect to the answer stream (HTTP ${response.status}).`,
            )
          }

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''
          let terminalEventType: 'complete' | 'error' | null = null

          for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const consumed = consumeStructuredStreamPayloads(buffer)
            buffer = consumed.remainingBuffer

            for (const event of consumed.payloads) {
              if (typeof event.eventId === 'number') {
                if (event.eventId <= activeGenerationEventIdRef.current) {
                  continue
                }
                activeGenerationEventIdRef.current = event.eventId
              }
              if (
                event.type === 'status' &&
                typeof event.content === 'string'
              ) {
                setStatusText(event.content)
              }
              if (event.type === 'complete' || event.type === 'error') {
                terminalEventType = event.type
                void reader.cancel()
              }
              if (event.type !== 'heartbeat' && event.type !== 'metadata') {
                await refreshConversationFromServer(conversationIdToRefresh)
              }
            }
          }

          const [refreshed, activeGeneration] = await Promise.all([
            refreshConversationFromServer(conversationIdToRefresh),
            getActiveChatGeneration({
              data: { conversationId: conversationIdToRefresh },
            }),
          ])

          if (
            terminalEventType !== null ||
            !activeGeneration ||
            activeGeneration.generation.id !== generationId
          ) {
            activeGenerationIdRef.current = null
            setIsLoading(false)
            if (refreshed) {
              setStatusText(
                terminalEventType === 'error'
                  ? 'Response could not be verified.'
                  : 'Answer complete.',
              )
            }
            return
          }
        } catch (error) {
          console.error('Failed to resume durable chat generation:', error)
          setStatusText('Reconnecting to your answer...')
          await new Promise((resolve) => window.setTimeout(resolve, 1_000))
          continue
        }

        // The server closed a non-terminal stream. Reconnect from the durable
        // event cursor; this does not imply cancellation or a new generation.
        await new Promise((resolve) => window.setTimeout(resolve, 250))
      }
    },
    [refreshConversationFromServer],
  )

  const closeCitationPanel = () => {
    setFilteredChunkIndices(null)
    setSelectedSourceReference(null)
    setIsCitationOpen(false)
  }

  const mobileCitationTitle =
    selectedSourceReference || filteredChunkIndices
      ? 'Selected source'
      : 'Sources & citations'

  // Sync messages with loader data only when intentionally changing conversation.
  // During an active stream we set hydratedConversationRef to avoid replacing partial text.
  useEffect(() => {
    if (conversationId === hydratedConversationRef.current) return
    dismissSourcesCoachmark('conversation_changed')

    const hydratedMessages: Array<HydratedMessage> =
      initialMessages.map(hydrateMessage)
    const activeConversation = initialConversations.find(
      (conversation) => conversation.id === conversationId,
    )
    const latestAssistantMessage = [...hydratedMessages]
      .reverse()
      .find((message) => message.role === 'assistant')

    setMessages(hydratedMessages)
    setActiveId(conversationId)
    setVisualEvidence(latestAssistantMessage?.visualEvidence ?? [])
    hydratedConversationRef.current = conversationId
  }, [
    dismissSourcesCoachmark,
    initialMessages,
    conversationId,
    initialConversations,
  ])

  useEffect(() => {
    const generationId = initialGeneration?.generation?.id
    if (!generationId || !conversationId) return

    activeGenerationIdRef.current = generationId
    activeGenerationEventIdRef.current = initialGeneration.lastEventId ?? 0
    void resumeGenerationFromServer(generationId, conversationId)

    return () => {
      if (activeGenerationIdRef.current === generationId) {
        activeGenerationIdRef.current = null
      }
    }
  }, [conversationId, initialGeneration, resumeGenerationFromServer])

  useEffect(() => {
    const container = scrollRef.current!

    if (messages.length === 0) {
      container.scrollTop = 0
      setShowJumpToLatest(false)
      return
    }

    updateLatestButtonVisibility()
  }, [messages, updateLatestButtonVisibility])

  useEffect(() => {
    const container = scrollRef.current!

    updateLatestButtonVisibility()
    container.addEventListener('scroll', updateLatestButtonVisibility, {
      passive: true,
    })
    return () => {
      container.removeEventListener('scroll', updateLatestButtonVisibility)
    }
  }, [updateLatestButtonVisibility])

  useEffect(() => {
    const content = messageContentRef.current!

    let frame: number | null = null
    const scheduleVisibilityUpdate = () => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(() => {
        frame = null
        updateLatestButtonVisibility()
      })
    }

    scheduleVisibilityUpdate()
    if (typeof ResizeObserver !== 'function') {
      return () => {
        if (frame !== null) {
          window.cancelAnimationFrame(frame)
        }
      }
    }

    const observer = new ResizeObserver(scheduleVisibilityUpdate)
    observer.observe(content)
    return () => {
      observer.disconnect()
      if (frame !== null) {
        window.cancelAnimationFrame(frame)
      }
    }
  }, [updateLatestButtonVisibility])

  useEffect(() => {
    const composer = bottomComposerRef.current
    if (!composer) return

    const updateComposerHeight = () => {
      const measuredHeight = Math.ceil(composer.getBoundingClientRect().height)
      if (measuredHeight > 0) {
        setComposerHeight(measuredHeight)
      }
    }

    updateComposerHeight()
    if (typeof ResizeObserver !== 'function') return

    const observer = new ResizeObserver(() => {
      updateComposerHeight()
    })
    observer.observe(composer)
    return () => observer.disconnect()
  }, [isDesktop, isLimitReached, isLoading, messages.length])

  // Auto-show paywall modal when limit is reached on page load
  useEffect(() => {
    // Only show once per session to prevent loop when user closes modal
    if (isLimitReached && !hasShownPaywallRef.current) {
      setShowPaywall(true)
      hasShownPaywallRef.current = true
      trackAnalyticsEvent('paywall_viewed', {
        reason: 'limit_reached',
        user_state: user ? 'signed_in' : 'anonymous',
        ...getUsageAnalyticsProperties(usage),
      })
    }
  }, [isLimitReached, usage, user])

  const handleSendMessage = async (
    query: string,
    requestedEntryPoint?: 'retry',
  ) => {
    setDraftPrompt(null)
    const entryPoint = requestedEntryPoint
      ? requestedEntryPoint
      : source === 'home'
        ? 'home'
        : activeId
          ? 'conversation'
          : 'chat'
    trackAnalyticsEvent('chat_query_submission_attempted', {
      entry_point: entryPoint,
      source: source === 'home' ? 'home_handoff' : 'composer',
      has_active_conversation: Boolean(activeId),
      visible_message_count: messages.length,
    })
    const abortController = new AbortController()
    activeStreamAbortControllerRef.current = abortController
    const newUserMessage: HydratedMessage = {
      role: 'user',
      content: query,
      createdAt: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, newUserMessage])
    setIsLoading(true)
    setStatusText('Searching documents...')
    setStatusReasoningSummary(null)

    const streamState = { hasStartedText: false }
    let assistantText = ''
    let assistantCreatedAt: string | null = null
    let assistantGroundingSupports: Array<GroundingSupport> | undefined
    let resolvedConversationId = activeId
    let generationId: string | null = null
    let assistantMessageId: string | null = null
    let slowConnectionTimeout: ReturnType<typeof setTimeout> | null = null
    const clearStreamWatchdogs = () => {
      if (slowConnectionTimeout) {
        clearTimeout(slowConnectionTimeout)
        slowConnectionTimeout = null
      }
    }
    const resetSlowConnectionWarning = () => {
      if (slowConnectionTimeout) clearTimeout(slowConnectionTimeout)
      slowConnectionTimeout = setTimeout(() => {
        setStatusReasoningSummary(null)
        setStatusText('Connection is slow. Waiting for more of the response...')
      }, STREAM_SLOW_WARNING_MS)
    }
    try {
      resetSlowConnectionWarning()
      const analyticsContext = await getAnalyticsRequestContext()

      const response = await fetch('/api/stream-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          history: messages,
          conversationId: activeId,
          planCheckRunId,
          analytics: {
            visitorId: analyticsContext.visitorId,
            sessionId: analyticsContext.sessionId,
            entryPoint,
            googleClientId: analyticsContext.googleClientId,
            googleSessionId: analyticsContext.googleSessionId,
            googleEngagementTimeMsec: analyticsContext.googleEngagementTimeMsec,
            amplitudeSessionId: analyticsContext.amplitudeSessionId,
          },
        }),
        signal: abortController.signal,
      })

      if (response.status === 429) {
        setShowPaywall(true)
        trackAnalyticsEvent('paywall_viewed', {
          reason: 'limit_reached',
          user_state: user ? 'signed_in' : 'anonymous',
          ...getUsageAnalyticsProperties(usage),
        })
        return
      }

      if (!response.ok) {
        throw await createChatHttpError(response)
      }

      if (!response.body) throw new Error('No response body')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let messageCitations: Array<GroundedCitation> = []
      let messageVisualEvidence: Array<any> = []
      let messageDiagramRequests: Array<any> = []
      let messageTableRequests: Array<any> = []
      let messageSourceReferenceRequests: Array<any> = []
      let assistantVerificationStatus: HydratedMessage['verificationStatus'] =
        'verifying'
      let assistantErrorCode: string | null = null
      let assistantErrorStage: string | null = null
      const handleStreamData = (data: any) => {
        if (typeof data.eventId === 'number') {
          if (data.eventId <= activeGenerationEventIdRef.current) return
          activeGenerationEventIdRef.current = data.eventId
        }
        if (data.type === 'text') {
          assistantText += data.content
          streamState.hasStartedText = true
          upsertAssistantMessage()
        } else if (data.type === 'reset') {
          assistantText = ''
          messageCitations = []
          assistantGroundingSupports = []
          messageVisualEvidence = []
          messageDiagramRequests = []
          messageTableRequests = []
          messageSourceReferenceRequests = []
          assistantVerificationStatus = 'verifying'
          assistantErrorCode = null
          assistantErrorStage = null
          streamState.hasStartedText = false
          setCitations([])
          setVisualEvidence([])
          upsertAssistantMessage()
        } else if (data.type === 'citations') {
          messageCitations = data.content
          assistantGroundingSupports = data.groundingSupports
          messageVisualEvidence = data.visualEvidence ?? []
          assistantVerificationStatus = 'verified'
          setCitations(data.content)
          setVisualEvidence(messageVisualEvidence)
          if (streamState.hasStartedText) {
            upsertAssistantMessage()
          }
        } else if (data.type === 'diagrams') {
          messageDiagramRequests = data.content ?? []
          setMessages((prev) => {
            const last = prev[prev.length - 1]
            if (prev.length === 0 || last.role !== 'assistant') return prev
            return [
              ...prev.slice(0, -1),
              {
                ...last,
                id: last.id || messageDiagramRequests[0]?.messageId,
                diagramRequests: messageDiagramRequests,
                tableRequests: messageTableRequests,
                sourceReferenceRequests: messageSourceReferenceRequests,
              },
            ]
          })
        } else if (data.type === 'tables') {
          messageTableRequests = data.content ?? []
          setMessages((prev) => {
            const last = prev[prev.length - 1]
            if (prev.length === 0 || last.role !== 'assistant') return prev
            return [
              ...prev.slice(0, -1),
              {
                ...last,
                id: last.id || messageTableRequests[0]?.messageId,
                diagramRequests: messageDiagramRequests,
                tableRequests: messageTableRequests,
                sourceReferenceRequests: messageSourceReferenceRequests,
              },
            ]
          })
        } else if (data.type === 'references') {
          messageSourceReferenceRequests = data.content ?? []
          setMessages((prev) => {
            const last = prev[prev.length - 1]
            if (prev.length === 0 || last.role !== 'assistant') return prev
            return [
              ...prev.slice(0, -1),
              {
                ...last,
                sourceReferenceRequests: messageSourceReferenceRequests,
              },
            ]
          })
        } else if (data.type === 'metadata') {
          if (data.usage) setUsage(data.usage)
          if (typeof data.generationId === 'string') {
            generationId = data.generationId
            activeGenerationIdRef.current = data.generationId
          }
          if (typeof data.assistantMessageId === 'string') {
            assistantMessageId = data.assistantMessageId
          }
          if (data.conversationId) {
            resolvedConversationId = data.conversationId
          }
          if (data.conversationId && data.conversationId !== activeId) {
            hydratedConversationRef.current = data.conversationId
            setActiveId(data.conversationId)
            navigate({
              to: '/chat',
              search: {
                conversationId: data.conversationId,
                ...scopedPlanCheckSearch,
              },
              replace: true,
            })
            if (!conversations.find((c) => c.id === data.conversationId)) {
              setConversations((prev) =>
                dedupeConversations([
                  {
                    id: data.conversationId,
                    title: query.substring(0, 50),
                  },
                  ...prev,
                ]),
              )
            }
          }
        } else if (data.type === 'status') {
          setStatusText(data.content)
          setStatusReasoningSummary(
            typeof data.reasoningSummary === 'string'
              ? data.reasoningSummary
              : null,
          )
        } else if (data.type === 'heartbeat') {
          return
        } else if (data.type === 'complete') {
          activeGenerationIdRef.current = null
          void reader.cancel()
          if (resolvedConversationId) {
            void refreshConversationFromServer(resolvedConversationId)
          }
        } else if (data.type === 'error') {
          activeGenerationIdRef.current = null
          const streamError = new Error(
            typeof data.content === 'string'
              ? data.content
              : 'Sorry, I encountered an error. Please try again later.',
          )
          assistantVerificationStatus = 'unverified'
          assistantErrorCode = typeof data.code === 'string' ? data.code : null
          assistantErrorStage =
            typeof data.stage === 'string' ? data.stage : null
          ;(
            streamError as Error & {
              code?: string
              stage?: string
              isStreamError?: boolean
            }
          ).code = typeof data.code === 'string' ? data.code : undefined
          ;(
            streamError as Error & {
              code?: string
              stage?: string
              isStreamError?: boolean
            }
          ).stage = typeof data.stage === 'string' ? data.stage : undefined
          ;(
            streamError as Error & {
              code?: string
              stage?: string
              isStreamError?: boolean
            }
          ).isStreamError = true
          throw streamError
        }
      }
      const upsertAssistantMessage = () => {
        setMessages((prev) => {
          assistantCreatedAt = assistantCreatedAt ?? new Date().toISOString()
          const nextAssistantMessage = {
            role: 'assistant' as const,
            id: assistantMessageId ?? undefined,
            content: assistantText,
            createdAt: assistantCreatedAt,
            citations: messageCitations,
            groundingSupports: assistantGroundingSupports,
            visualEvidence: messageVisualEvidence,
            diagramRequests: messageDiagramRequests,
            tableRequests: messageTableRequests,
            sourceReferenceRequests: messageSourceReferenceRequests,
            verificationStatus: assistantVerificationStatus,
            errorCode: assistantErrorCode,
            errorStage: assistantErrorStage,
          }
          const last = prev[prev.length - 1]
          if (last.role === 'assistant') {
            return [...prev.slice(0, -1), { ...last, ...nextAssistantMessage }]
          }
          return [...prev, nextAssistantMessage]
        })
      }

      for (;;) {
        const { done, value } = await reader.read()
        if (done) {
          buffer += decoder.decode()
          const consumed = consumeStructuredStreamPayloads(buffer, true)
          for (const data of consumed.payloads) {
            handleStreamData(data)
          }
          break
        }

        resetSlowConnectionWarning()

        buffer += decoder.decode(value, { stream: true })
        const consumed = consumeStructuredStreamPayloads(buffer)
        buffer = consumed.remainingBuffer

        for (const data of consumed.payloads) {
          handleStreamData(data)
        }
      }

      if (
        !streamState.hasStartedText &&
        (messageCitations.length > 0 || messageVisualEvidence.length > 0)
      ) {
        setIsCitationOpen(true)
      }

      if (
        !streamState.hasStartedText &&
        messageCitations.length === 0 &&
        messageVisualEvidence.length === 0
      ) {
        const incompleteStreamError = new Error(
          'Sorry, the response ended before an answer was received. Please try again.',
        ) as Error & {
          code?: string
          stage?: string
          isStreamError?: boolean
        }
        incompleteStreamError.code = 'stream_ended_without_answer'
        incompleteStreamError.stage = 'receiving_stream'
        incompleteStreamError.isStreamError = true
        throw incompleteStreamError
      }
    } catch (e) {
      console.error('Chat Error:', e)
      const caughtError = e
      const errorName = readErrorString(caughtError, 'name')
      const isAbortError = errorName === 'AbortError'
      const isManualCancel = abortController.signal.aborted
      const isStreamError = isMarkedStreamError(caughtError)
      const streamErrorMessage = readErrorString(caughtError, 'message')
      const streamErrorCode = readErrorString(caughtError, 'code')
      const streamErrorStage = readErrorString(caughtError, 'stage')
      const isHttpStartError = /^http_\d{3}$/.test(streamErrorCode ?? '')
      const errorContent = isAbortError
        ? isManualCancel
          ? 'Response stopped.'
          : 'Sorry, the connection was interrupted. Please try again.'
        : (isStreamError || isHttpStartError) && streamErrorMessage
          ? streamErrorMessage
          : isManualCancel
            ? 'Response stopped.'
            : 'Sorry, I encountered an error. Please try again later.'
      const shouldAttemptRecovery =
        !isStreamError && !isManualCancel && Boolean(generationId)
      if (shouldAttemptRecovery && resolvedConversationId) {
        setStatusText(
          'Connection interrupted. The answer is still being prepared...',
        )
        void resumeGenerationFromServer(generationId, resolvedConversationId)
        return
      }
      if (
        !isManualCancel &&
        !isHttpStartError &&
        streamState.hasStartedText &&
        resolvedConversationId
      ) {
        // Refresh the persisted draft/error after a stream-level failure. This
        // also keeps legacy direct responses recoverable while old instances
        // drain during rollout.
        const recovered = await refreshConversationFromServer(
          resolvedConversationId,
        )
        if (recovered) return
      }
      if (streamState.hasStartedText) {
        setMessages((prev) => {
          const last = prev[prev.length - 1]
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              citations: [],
              groundingSupports: [],
              visualEvidence: [],
              sourceReferenceRequests: [],
              verificationStatus: 'unverified',
              errorCode: isManualCancel
                ? 'user_cancelled'
                : (streamErrorCode ?? last.errorCode),
              errorStage: isManualCancel
                ? 'client_stream_cancelled'
                : (streamErrorStage ?? last.errorStage),
            },
          ]
        })
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: errorContent,
            createdAt: new Date().toISOString(),
            tableRequests: [],
            sourceReferenceRequests: [],
            verificationStatus: 'unverified',
            errorCode: isManualCancel
              ? 'user_cancelled'
              : (streamErrorCode ?? null),
            errorStage: isManualCancel
              ? 'client_stream_cancelled'
              : (streamErrorStage ?? null),
          },
        ])
      }
    } finally {
      clearStreamWatchdogs()
      if (activeStreamAbortControllerRef.current === abortController) {
        activeStreamAbortControllerRef.current = null
      }
      setStatusReasoningSummary(null)
      if (!activeGenerationIdRef.current) {
        setIsLoading(false)
      }
    }
  }

  const handleCancelStream = () => {
    trackAnalyticsEvent('chat_stream_cancelled', {
      has_active_conversation: Boolean(activeId),
    })
    setStatusReasoningSummary(null)
    setStatusText('Stopping response...')
    const generationId = activeGenerationIdRef.current
    if (generationId) {
      void fetch(`/api/chat-generations/${generationId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }).catch((error) => {
        console.error('Failed to request durable response cancellation:', error)
      })
    }
    activeGenerationIdRef.current = null
    activeStreamAbortControllerRef.current?.abort(
      new DOMException('Response stopped.', 'AbortError'),
    )
  }

  useEffect(() => {
    if (autoSentFromHomeRef.current) return
    if (source !== 'home') return
    if (conversationId) return
    if (messages.length > 0) return

    const handoffPrompt = consumeHomeChatHandoff()
    if (!handoffPrompt) return

    autoSentFromHomeRef.current = true
    void handleSendMessage(handoffPrompt)
  }, [source, conversationId, messages.length, handleSendMessage])

  const handleUpgrade = () => {
    trackAnalyticsEvent('upgrade_clicked', {
      location: 'chat_paywall',
      reason: 'limit_reached',
      target_plan_id: 'proUser',
      ...getUsageAnalyticsProperties(usage),
    })
    // Redirect to the pro checkout flow
    navigate({ to: '/checkout/$planId', params: { planId: 'proUser' } })
  }

  const startNewChat = () => {
    setDraftPrompt(null)
    setIsMobileHistoryOpen(false)
    navigate({ to: '/chat', search: scopedPlanCheckSearch, replace: true })
  }

  const handleDeleteConversation = async (conversationIdToDelete: string) => {
    try {
      await deleteConversation({
        data: { conversationId: conversationIdToDelete },
      })
      setConversations((prev) =>
        prev.filter(
          (conversation) => conversation.id !== conversationIdToDelete,
        ),
      )

      if (activeId === conversationIdToDelete) {
        hydratedConversationRef.current = undefined
        setActiveId(undefined)
        setMessages([])
        setDraftPrompt(null)
        setCitations([])
        setVisualEvidence([])
        setIsCitationOpen(false)
        setFilteredChunkIndices(null)
        setSelectedSourceReference(null)
        setIsMobileHistoryOpen(false)
        navigate({ to: '/chat', search: {}, replace: true })
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error)
    }
  }

  const handleLogin = () => {
    trackAnalyticsEvent('login_clicked', {
      location: 'chat_paywall',
      reason: 'limit_reached',
    })
    navigate({ to: '/login' })
  }

  const openSourcePdfPreview = (citation: CitationArtifact) => {
    const pageNumber = citation.pageStart ?? citation.pageEnd
    if (!isDesktop && citation.pageImageUrl) {
      setArtifactPreview({
        kind: 'page-image',
        title: citation.title,
        url: citation.pageImageUrl,
        pageNumber,
      })
      return
    }
    setArtifactPreview({
      kind: 'pdf',
      title: citation.title,
      url: withPdfPageFragment(citation.sourcePdfUrl!, pageNumber),
      sourceUrl: citation.sourcePdfUrl!,
      pageNumber,
    })
  }

  const downloadSourcePage = (citation: CitationArtifact) => {
    const pageNumber = citation.pageStart ?? citation.pageEnd
    const downloadUrl = buildSinglePageDownloadUrl(
      citation.sourcePdfUrl!,
      pageNumber,
    )!
    window.open(downloadUrl, '_blank', 'noopener,noreferrer')
  }

  const openPageImagePreview = (diagramRequest: {
    documentTitle?: string | null
    pageNumber?: number
    fullPageImageUrl?: string | null
  }) => {
    trackAnalyticsEvent('page_image_preview_opened', {
      source_context: 'diagram',
      has_page: Boolean(diagramRequest.pageNumber),
    })
    setArtifactPreview({
      kind: 'page-image',
      title: diagramRequest.documentTitle || 'Source page image',
      url: diagramRequest.fullPageImageUrl!,
      pageNumber: diagramRequest.pageNumber,
    })
  }

  const openSourceReference = async (request: any) => {
    const referenceText =
      typeof request?.referenceText === 'string'
        ? request.referenceText
        : 'Source reference'
    setSelectedSourceReference({
      status: 'loading',
      referenceText,
    })
    setFilteredChunkIndices(null)
    setIsCitationOpen(true)
    trackAnalyticsEvent('source_reference_resolution_started', {
      has_request_id: Boolean(request?.id),
    })

    try {
      const resolved = await resolveSourceReferenceRequest({
        data: { requestId: request.id },
      })
      trackAnalyticsEvent('source_reference_resolution_finished', {
        status: resolved.status,
        has_page: Boolean(
          resolved.resolvedPageStart || resolved.resolvedPageEnd,
        ),
      })
      setSelectedSourceReference(
        resolved.status === 'failed'
          ? { ...resolved, status: 'failed' }
          : { ...resolved, status: 'ready' },
      )

      setMessages((prev) =>
        prev.map((message) =>
          replaceResolvedSourceReference(message, resolved),
        ),
      )
    } catch (error) {
      console.error('Failed to resolve source reference:', error)
      trackAnalyticsEvent('source_reference_resolution_finished', {
        status: 'failed',
        has_page: false,
      })
      setSelectedSourceReference({
        status: 'failed',
        referenceText,
        failureReason:
          'The exact source reference could not be retrieved right now.',
      })
    }
  }

  useEffect(() => {
    const pendingMessageIds = messages
      .filter((message) =>
        (message.diagramRequests ?? []).some(
          (request) =>
            request.status !== 'ready' && request.status !== 'failed',
        ),
      )
      .map((message) => message.id)
      .filter((value): value is string => Boolean(value))

    if (!pendingMessageIds.length) {
      return
    }

    const poll = async () => {
      const nextRequests = await getDiagramRequests({
        data: { messageIds: pendingMessageIds },
      })

      setMessages((prev) =>
        prev.map((message) => {
          if (!message.id) return message
          const diagramRequests = nextRequests.filter(
            (request) => request.messageId === message.id,
          )
          return diagramRequests.length > 0
            ? { ...message, diagramRequests }
            : message
        }),
      )
    }

    void poll()
    diagramPollingRef.current = window.setInterval(() => {
      void poll()
    }, 500)

    return () => {
      if (diagramPollingRef.current) {
        window.clearInterval(diagramPollingRef.current)
        diagramPollingRef.current = null
      }
    }
  }, [messages])

  useEffect(() => {
    const pendingMessageIds = messages
      .filter((message) =>
        (message.tableRequests ?? []).some(
          (request) =>
            request.status !== 'ready' && request.status !== 'failed',
        ),
      )
      .map((message) => message.id)
      .filter((value): value is string => Boolean(value))

    if (!pendingMessageIds.length) {
      return
    }

    const poll = async () => {
      const nextRequests = await getTableRequests({
        data: { messageIds: pendingMessageIds },
      })

      setMessages((prev) =>
        prev.map((message) => {
          if (!message.id) return message
          const tableRequests = nextRequests.filter(
            (request) => request.messageId === message.id,
          )
          return tableRequests.length > 0
            ? { ...message, tableRequests }
            : message
        }),
      )
    }

    void poll()
    tablePollingRef.current = window.setInterval(() => {
      void poll()
    }, 500)

    return () => {
      if (tablePollingRef.current) {
        window.clearInterval(tablePollingRef.current)
        tablePollingRef.current = null
      }
    }
  }, [messages])

  const isMobileCitationSheetOpen = !isDesktop && isCitationOpen
  const showBottomComposer = !isMobileCitationSheetOpen
  const mobileKeyboardInset = isDesktop ? 0 : keyboardViewportInset.bottomInset
  const isMobileKeyboardOpen =
    !isDesktop && keyboardViewportInset.isKeyboardOpen
  const messagePaneBottomInset = isDesktop
    ? composerHeight + CHAT_MESSAGE_BOTTOM_BUFFER_PX
    : CHAT_MOBILE_MESSAGE_BOTTOM_BUFFER_PX
  const messagePaneScrollPaddingBottom = isDesktop
    ? `calc(var(--chat-composer-height) + env(safe-area-inset-bottom) + ${CHAT_MESSAGE_BOTTOM_BUFFER_PX}px)`
    : `calc(env(safe-area-inset-bottom) + var(--chat-keyboard-inset) + ${CHAT_MOBILE_MESSAGE_BOTTOM_BUFFER_PX}px)`
  const scrollToLatest = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      const container = scrollRef.current!
      if (typeof container.scrollTo === 'function') {
        container.scrollTo({
          top: container.scrollHeight,
          behavior,
        })
      } else {
        container.scrollTop = container.scrollHeight
      }

      window.requestAnimationFrame(updateLatestButtonVisibility)
    },
    [updateLatestButtonVisibility],
  )

  const dismissPaywall = () => {
    trackAnalyticsEvent('paywall_dismissed', {
      reason: 'limit_reached',
      user_state: user ? 'signed_in' : 'anonymous',
      ...getUsageAnalyticsProperties(usage),
    })
    setShowPaywall(false)
  }
  const closeArtifactPreview = () => setArtifactPreview(null)

  return (
    <div className="fixed inset-0 flex max-w-full flex-col overflow-hidden bg-background font-sans text-foreground">
      <div className="hidden lg:block">
        <Header
          rightSlot={
            hasAssistantResponse ? (
              <SourcesToggle
                isSelected={sourcesVisible}
                onChange={changeSourcesVisible}
                analyticsLocation="desktop_header"
              />
            ) : null
          }
        />
      </div>

      <main
        id="main-content"
        className="flex min-h-0 flex-1 max-w-full overflow-hidden"
      >
        {/* Paywall Modal */}
        {showPaywall ? (
          <DialogContainer onDismiss={dismissPaywall}>
            <CustomDialog isDismissible aria-label="Upgrade your plan">
              <div className="space-y-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  {user ? 'Usage limit reached' : 'Visitor limit reached'}
                </h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {user
                    ? "You've reached your daily limit for the Free tier. Upgrade to Pro for unlimited access."
                    : "You've reached the limit for guest users. Sign up for a free account to continue chatting."}
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    fillStyle="outline"
                    onPress={dismissPaywall}
                  >
                    Close
                  </Button>
                  {user ? (
                    <Button onPress={handleUpgrade} isDisabled={isLoading}>
                      {isLoading ? 'Upgrading...' : 'Upgrade to Pro'}
                    </Button>
                  ) : (
                    <Button onPress={handleLogin}>Sign up / login</Button>
                  )}
                </div>
              </div>
            </CustomDialog>
          </DialogContainer>
        ) : null}

        {artifactPreview ? (
          <DialogContainer onDismiss={closeArtifactPreview}>
            <CustomDialog
              isDismissible
              aria-label="Source PDF preview"
              styles={artifactPreviewDialogStyles}
            >
              <div className="flex h-[82vh] max-h-[82vh] min-h-0 flex-col overflow-hidden bg-background">
                <div className="border-b border-border/80 px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h2 className="truncate text-lg font-semibold text-foreground">
                        Source PDF
                      </h2>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <p className="min-w-0 truncate">
                          {artifactPreview.title}
                          {artifactPreview.pageNumber
                            ? ` · Page ${artifactPreview.pageNumber}`
                            : ''}
                        </p>
                        {artifactPreview.kind === 'pdf' &&
                        artifactPreview.pageNumber ? (
                          <Button
                            variant="secondary"
                            fillStyle="outline"
                            size="S"
                            onPress={() => {
                              const downloadUrl = buildSinglePageDownloadUrl(
                                artifactPreview.sourceUrl,
                                artifactPreview.pageNumber,
                              )
                              window.open(
                                downloadUrl!,
                                '_blank',
                                'noopener,noreferrer',
                              )
                            }}
                          >
                            Download page
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      fillStyle="outline"
                      size="S"
                      onPress={closeArtifactPreview}
                    >
                      Close
                    </Button>
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-hidden bg-neutral-100 p-4 dark:bg-neutral-950">
                  {artifactPreview.kind === 'pdf' ? (
                    <iframe
                      src={artifactPreview.url}
                      title={artifactPreview.title}
                      className="h-full w-full rounded-2xl border border-border/70 bg-white shadow-sm"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center overflow-auto rounded-2xl border border-border/70 bg-white shadow-sm">
                      <img
                        src={artifactPreview.url}
                        alt={artifactPreview.title}
                        className="max-h-full w-auto max-w-full object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            </CustomDialog>
          </DialogContainer>
        ) : null}

        {/* Mobile History Sheet */}
        {!isDesktop && isMobileHistoryOpen ? (
          <MobileHistoryDrawer
            title="Navigation"
            onClose={() => {
              trackAnalyticsEvent('chat_history_closed', {
                location: 'mobile_sheet',
              })
              setIsMobileHistoryOpen(false)
            }}
            headerContent={
              <div className="flex items-center gap-2">
                <Link
                  to="/"
                  onClick={() => {
                    trackAnalyticsEvent('navigation_clicked', {
                      destination: '/',
                      label: 'home',
                      location: 'mobile_chat_sheet',
                    })
                    setIsMobileHistoryOpen(false)
                  }}
                  className="inline-flex items-center rounded-full border border-border/80 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60"
                >
                  Home
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => {
                    trackAnalyticsEvent('navigation_clicked', {
                      destination: '/pricing',
                      label: 'pricing',
                      location: 'mobile_chat_sheet',
                    })
                    setIsMobileHistoryOpen(false)
                  }}
                  className="inline-flex items-center rounded-full border border-border/80 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60"
                >
                  Pricing
                </Link>
                <a
                  href="mailto:info@buildingregs.app"
                  onClick={() => {
                    trackAnalyticsEvent('navigation_clicked', {
                      destination: 'mailto:info@buildingregs.app',
                      label: 'help',
                      location: 'mobile_chat_sheet',
                    })
                    setIsMobileHistoryOpen(false)
                  }}
                  className="inline-flex items-center rounded-full border border-border/80 px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60"
                >
                  Help
                </a>
              </div>
            }
          >
            <div className="flex h-full min-h-0 flex-col bg-background">
              <div className="min-h-0 flex-1">
                <MobileChatHistory
                  history={conversations}
                  activeConversationId={activeId}
                  onNewChat={startNewChat}
                  onDeleteConversation={handleDeleteConversation}
                  onSelect={() => setIsMobileHistoryOpen(false)}
                  showTitle={false}
                  alwaysShowActions
                />
              </div>
            </div>
          </MobileHistoryDrawer>
        ) : null}

        {/* Sidebar (Desktop) */}
        <div className="hidden lg:block">
          <ChatSidebar
            history={conversations}
            activeConversationId={activeId}
            onNewChat={startNewChat}
            onDeleteConversation={handleDeleteConversation}
          />
        </div>

        {/* Main Chat Area */}
        <div
          className="relative flex min-w-0 flex-1 flex-col overflow-x-hidden bg-background"
          style={
            {
              '--chat-composer-height': `${composerHeight}px`,
              '--chat-keyboard-inset': `${mobileKeyboardInset}px`,
            } as CSSProperties
          }
        >
          {/* Mobile Header */}
          <div
            className="relative z-20 grid shrink-0 grid-cols-[auto_1fr_auto] items-center gap-2 border-b border-border/80 bg-background/95 px-4 pb-3 backdrop-blur lg:hidden"
            data-safe-area-header="true"
            data-testid="mobile-chat-header"
            style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
          >
            <div className="flex min-w-0 justify-start">
              <button
                type="button"
                className="inline-flex min-h-11 items-center gap-3 rounded-md bg-transparent px-1 text-foreground transition-colors hover:text-foreground/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-foreground"
                onClick={() => {
                  trackAnalyticsEvent('chat_history_opened', {
                    location: 'mobile_header',
                    history_count: conversations.length,
                  })
                  setIsMobileHistoryOpen(true)
                }}
                aria-label="Open chat history"
                aria-expanded={isMobileHistoryOpen}
                aria-controls="mobile-chat-history-drawer"
              >
                <Menu className="h-5 w-5" aria-hidden />
                <span
                  aria-hidden="true"
                  data-testid="mobile-chat-brand"
                  className="pointer-events-none flex h-7 w-7 items-center justify-center rounded-md bg-foreground text-[10px] font-bold text-background"
                >
                  BR
                </span>
              </button>
            </div>
            <div aria-hidden="true" />
            <div className="flex min-w-0 items-center justify-end gap-3">
              {hasAssistantResponse ? (
                <SourcesToggle
                  isSelected={sourcesVisible}
                  onChange={changeSourcesVisible}
                  size="M"
                  analyticsLocation="mobile_header"
                />
              ) : null}
              <BetterAuthHeader accountButtonSize="M" />
            </div>
          </div>

          <div
            className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
            data-testid="chat-message-pane"
            ref={scrollRef}
            style={{
              scrollPaddingBottom: messagePaneScrollPaddingBottom,
            }}
          >
            <div
              ref={messageContentRef}
              className="mx-auto flex min-h-full w-full max-w-3xl flex-col space-y-6 px-4 pt-5 md:px-8 md:pt-8"
              style={{
                paddingBottom: `calc(${messagePaneBottomInset}px + env(safe-area-inset-bottom))`,
              }}
            >
              {planCheckRunId ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950 shadow-sm dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-100">
                  <div className="font-semibold">Plan-check context active</div>
                  <div className="mt-1 text-blue-800 dark:text-blue-200">
                    {planCheckTitle
                      ? `Answers can use the assessment matrix for ${planCheckTitle}.`
                      : 'Answers can use the assessment matrix for this uploaded plan.'}
                  </div>
                </div>
              ) : null}
              {messages.length === 0 ? (
                <div className="animate-in fade-in zoom-in-95 flex flex-1 flex-col items-center justify-start pb-16 pt-2 text-center duration-500 lg:justify-center lg:pb-20 lg:pt-0">
                  <div
                    className={cn(
                      'flex items-center justify-center rounded-2xl bg-blue-600 shadow-xl shadow-blue-500/20',
                      isDesktop ? 'mb-8 h-16 w-16' : 'mb-5 h-14 w-14',
                    )}
                  >
                    <MessageSquare
                      className={cn(
                        'text-white',
                        isDesktop ? 'h-8 w-8' : 'h-7 w-7',
                      )}
                    />
                  </div>
                  <h1
                    className={cn(
                      'font-bold tracking-tight text-foreground',
                      isDesktop ? 'mb-8 text-3xl lg:text-4xl' : 'mb-6 text-2xl',
                    )}
                  >
                    What can I help with?
                  </h1>
                  <QuickPromptCards
                    prompts={emptyStateQuickPrompts}
                    testId="chat-empty-quick-prompt"
                    className="mt-6 grid w-full max-w-2xl gap-3 sm:grid-cols-3"
                    onSelect={(prompt) => {
                      trackAnalyticsEvent('chat_empty_quick_prompt_selected', {
                        prompt_index: emptyStateQuickPrompts.indexOf(prompt),
                      })
                      setDraftPrompt({
                        key: Date.now(),
                        value: prompt,
                      })
                    }}
                  />
                </div>
              ) : (
                <>
                  {messages.map((msg, i) => {
                    const previousUserMessage =
                      msg.role === 'assistant'
                        ? messages
                            .slice(0, i)
                            .reverse()
                            .find((previousMsg) => previousMsg.role === 'user')
                            ?.content
                        : null
                    const conversationUrl = getCurrentConversationUrl()

                    const messageKey =
                      msg.id ?? `${activeId ?? 'new'}-${msg.role}-${i}`

                    return (
                      <div key={messageKey} className="min-w-0 max-w-full">
                        <ChatMessage
                          messageId={msg.id}
                          role={msg.role}
                          content={msg.content}
                          createdAt={msg.createdAt}
                          rawContent={msg.rawContent}
                          citations={msg.citations}
                          groundingSupports={msg.groundingSupports}
                          visualEvidence={msg.visualEvidence}
                          diagramRequests={msg.diagramRequests}
                          tableRequests={msg.tableRequests}
                          sourceReferenceRequests={msg.sourceReferenceRequests}
                          verificationStatus={msg.verificationStatus}
                          onDiagramClick={openPageImagePreview}
                          onOpenSourcePdf={openSourcePdfPreview}
                          onOpenSourceReference={openSourceReference}
                          sourcesVisible={sourcesVisible}
                          feedback={msg.feedback}
                          previousUserMessage={previousUserMessage}
                          conversationUrl={conversationUrl}
                          onRetry={
                            msg.role === 'assistant' &&
                            msg.verificationStatus === 'unverified' &&
                            previousUserMessage
                              ? () =>
                                  void handleSendMessage(
                                    previousUserMessage,
                                    'retry',
                                  )
                              : undefined
                          }
                          onSourceEvidenceOpen={() =>
                            dismissSourcesCoachmark('source_clicked')
                          }
                          onCitationClick={(chunkIndices) => {
                            applyCitationSelection(
                              msg.citations!,
                              chunkIndices,
                              msg.visualEvidence ?? [],
                              (selection) => {
                                setCitations(selection.citations)
                                setVisualEvidence(selection.visualEvidence)
                                setFilteredChunkIndices(selection.chunkIndices)
                                setSelectedSourceReference(null)
                                setIsCitationOpen(true)
                              },
                            )
                          }}
                        />
                        {i === personaPromptAfterIndex ? (
                          <div className="mt-4">
                            <PersonaPromptCard
                              onComplete={() => setShowPersonaPrompt(false)}
                              onDismiss={() => setShowPersonaPrompt(false)}
                            />
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
                  {isLoading &&
                    messages[messages.length - 1]?.role !== 'assistant' && (
                      <div className="flex justify-start animate-in fade-in slide-in-from-bottom-2">
                        <div className="flex items-center gap-3 rounded-2xl border border-border/80 bg-card/90 p-4 text-sm italic text-muted-foreground shadow-sm">
                          <div className="flex gap-1">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce"></span>
                          </div>
                          {formatStatusText(statusText, statusReasoningSummary)}
                        </div>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>

          {showJumpToLatest && showBottomComposer ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20">
              <div
                className="pointer-events-none mx-auto flex w-full max-w-3xl justify-center px-4 md:px-8"
                style={{
                  paddingBottom: `calc(var(--chat-composer-height) + env(safe-area-inset-bottom) + var(--chat-keyboard-inset) + 0.75rem)`,
                }}
              >
                <button
                  type="button"
                  className="pointer-events-auto inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/80 bg-background text-foreground shadow-lg transition-colors hover:bg-secondary/70"
                  aria-label="Jump to latest message"
                  data-testid="chat-jump-to-latest"
                  onClick={() => {
                    trackAnalyticsEvent('jump_to_latest_clicked', {
                      location: isDesktop ? 'desktop_chat' : 'mobile_chat',
                    })
                    scrollToLatest()
                  }}
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}

          {sourcesCoachmark ? (
            <SourcesCoachmark
              placement={sourcesCoachmark.placement}
              targetRect={sourcesCoachmark.targetRect}
              targetRef={sourcesCoachmarkTargetRef}
              onDismiss={() => dismissSourcesCoachmark('got_it')}
            />
          ) : null}

          {showBottomComposer && (
            <div
              ref={bottomComposerRef}
              className={cn(
                'shrink-0 animate-in border-t border-border/80 bg-background/95 px-4 backdrop-blur slide-in-from-bottom-10 duration-500',
                isMobileKeyboardOpen
                  ? 'pb-2 pt-2'
                  : 'pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-4',
              )}
              data-testid="chat-bottom-composer"
              data-keyboard-open={isMobileKeyboardOpen ? 'true' : 'false'}
              style={{
                marginBottom: 'var(--chat-keyboard-inset)',
              }}
            >
              <div className="max-w-3xl mx-auto">
                <ChatInput
                  onSubmit={handleSendMessage}
                  onCancel={handleCancelStream}
                  isLoading={isLoading}
                  isDisabled={isLimitReached}
                  draftValue={
                    messages.length === 0 ? draftPrompt?.value : undefined
                  }
                  draftKey={
                    messages.length === 0 ? draftPrompt?.key : undefined
                  }
                  disabledCtaText={user ? 'Upgrade' : 'Sign up'}
                  onDisabledClick={() => {
                    setShowPaywall(true)
                    trackAnalyticsEvent('paywall_viewed', {
                      reason: 'limit_reached',
                      user_state: user ? 'signed_in' : 'anonymous',
                      ...getUsageAnalyticsProperties(usage),
                    })
                  }}
                />
                <p
                  className={cn(
                    'text-center text-muted-foreground',
                    isMobileKeyboardOpen ? 'mt-1 text-[11px]' : 'mt-2 text-xs',
                  )}
                >
                  AI response may be inaccurate. Always verify.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Citation Sidebar (Desktop) */}
        {isDesktop && (
          <div
            className={cn(
              'hidden h-full shrink-0 overflow-hidden border-l border-border bg-background transition-[width] duration-300 md:block',
              isCitationOpen ? 'w-[420px] xl:w-[460px]' : 'w-0 border-l-0',
            )}
          >
            <div className="h-full w-full">
              <CitationPanel
                citations={citations}
                visualEvidence={visualEvidence}
                sourceReference={selectedSourceReference}
                filteredChunkIndices={filteredChunkIndices}
                onOpenSourcePdf={openSourcePdfPreview}
                onDownloadSourcePage={downloadSourcePage}
                onClose={closeCitationPanel}
                highlightLabel={null}
              />
            </div>
          </div>
        )}

        {/* Citation Sheet (Mobile) */}
        {!isDesktop &&
          (isCitationOpen ? (
            <MobileSheet
              title={mobileCitationTitle}
              onClose={closeCitationPanel}
            >
              <CitationPanel
                citations={citations}
                visualEvidence={visualEvidence}
                sourceReference={selectedSourceReference}
                filteredChunkIndices={filteredChunkIndices}
                onOpenSourcePdf={openSourcePdfPreview}
                onDownloadSourcePage={downloadSourcePage}
                onClose={closeCitationPanel}
                highlightLabel={null}
                showHeader={false}
              />
            </MobileSheet>
          ) : null)}
      </main>
    </div>
  )
}

export const __chatRouteTestUtils = {
  activateCoachmarkPulse,
  applyCitationSelection,
  MobileHistoryDrawer,
  MobileSheet,
  buildChatConversationReturnTo,
  buildSinglePageDownloadUrl,
  consumeStructuredStreamPayloads,
  createChatHttpError,
  dedupeConversations,
  getStoredSourcesVisible,
  getCurrentConversationUrl,
  getCoachmarkTarget,
  hydrateMessage,
  isElementVisibleInContainer,
  isMarkedStreamError,
  parseLegacyJsonLine,
  parseSseBlock,
  readErrorString,
  replaceResolvedSourceReference,
  toCoachmarkRect,
  updateConnectedCoachmarkTarget,
  withPdfPageFragment,
}
